import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';
import { log2p1 } from '../../utils/transforms';
import {
  SAMPLE_GROUPS, GROUP_MARKERS, STRIP_GROUP_ORDER, STRIP_GROUP_LABELS,
  IMMGEN_COLORS,
} from '../../utils/colors';

const nGroups = STRIP_GROUP_ORDER.length;
const tickVals = [...Array(nGroups).keys()];

function jitter(x, n, seed = 42) {
  const vals = [];
  let s = seed;
  for (let i = 0; i < n; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    vals.push(x + (s / 0x7fffffff - 0.5) * 0.3);
  }
  return vals;
}

const EXAMPLE_GENES = [
  'Bcl6', 'Myc', 'Ezh2', 'Aicda', 'Gna13', 'S1pr2',
  'Foxo1', 'Pax5', 'Cd274', 'Mki67', 'Crebbp', 'Prdm1',
];

// Bar colors for 12 columns
const BAR_COLORS = [
  '#F39C12', '#2E86AB', '#27AE60', '#800000',  // ImmGen: Fo, CB, CC, PB
  '#3498DB', '#E67E22',                          // A20: vitro, vivo
  '#7FB069', '#D4A017', '#20B2AA',               // EmBO genotypes: WT, BCL2, Ezh2
  '#E74C3C', '#8E44AD', '#2ECC71',               // EmBO transformed: P0, P6, P6+Taz
];

export default function GeneDetail({ gene, onSelectGene }) {
  const { data, getFilteredData } = useData();

  const { filteredExpression, filteredColLabels, visibleIndices } = useMemo(
    () => getFilteredData(),
    [getFilteredData]
  );

  const info = useMemo(() => {
    if (!data || !gene) return null;

    const expr = filteredExpression?.[gene];
    const samples = data.perSample[gene];
    const deStats = {};
    if (data.deResults) {
      for (const [compId, comp] of Object.entries(data.deResults)) {
        const geneDE = comp.genes[gene];
        if (geneDE) {
          deStats[compId] = { ...geneDE, label: comp.label };
        }
      }
    }

    const panelMemberships = [];
    for (const [panelName, subcats] of Object.entries(data.panels)) {
      for (const [subcat, genes] of Object.entries(subcats)) {
        if (genes.includes(gene)) {
          panelMemberships.push({ panel: panelName, subcat });
        }
      }
    }

    return { expr, samples, deStats, panelMemberships };
  }, [data, gene, filteredExpression]);

  if (!gene) {
    return (
      <div className="gene-detail-empty">
        <p>Search for a gene to see its expression profile</p>
        <div className="example-genes">
          {EXAMPLE_GENES.map(g => (
            <button key={g} className="gene-chip" onClick={() => onSelectGene?.(g)}>
              <em>{g}</em>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (!info || !info.expr) {
    return (
      <div className="gene-detail-empty">
        <p>Gene <strong>{gene}</strong> not found in expression data</p>
        <p className="hint">Only genes with max TPM &gt; 1 are included</p>
      </div>
    );
  }

  const { expr, samples, deStats, panelMemberships } = info;
  const nCols = filteredColLabels.length;

  // Bar chart: filtered columns
  const filteredBarColors = visibleIndices.map(i => BAR_COLORS[i]);

  const barTrace = {
    type: 'bar',
    x: filteredColLabels,
    y: expr.map(log2p1),
    marker: { color: filteredBarColors },
    hovertext: expr.map((v, i) => `${filteredColLabels[i]}: ${v.toFixed(1)} TPM`),
    hoverinfo: 'text',
  };

  // Strip plot: per-sample data
  const stripTraces = [];
  if (samples) {
    const grouped = {};
    for (const g of STRIP_GROUP_ORDER) grouped[g] = [];
    for (const [sampleName, tpm] of Object.entries(samples)) {
      const group = SAMPLE_GROUPS[sampleName];
      if (group && grouped[group]) {
        grouped[group].push({ sample: sampleName, value: log2p1(tpm) });
      }
    }

    for (const group of STRIP_GROUP_ORDER) {
      const points = grouped[group];
      if (points.length === 0) continue;
      const marker = GROUP_MARKERS[group];
      const xBase = STRIP_GROUP_ORDER.indexOf(group);
      const xs = jitter(xBase, points.length, gene.charCodeAt(0) + xBase * 100);

      stripTraces.push({
        type: 'scatter',
        mode: 'markers',
        x: xs,
        y: points.map(p => p.value),
        marker: {
          symbol: marker.symbol,
          color: marker.color,
          size: 10,
          line: { width: 0.8, color: '#fff' },
        },
        name: marker.name,
        hovertext: points.map(p => `${p.sample}: ${p.value.toFixed(2)}`),
        hoverinfo: 'text',
      });
    }
  }

  // ImmGen reference shapes for strip (use full expression data for reference lines)
  const fullExpr = data.expression[gene];
  const refShapes = fullExpr ? [
    { y: log2p1(fullExpr[0]), color: IMMGEN_COLORS.Fo, label: 'Fo' },
    { y: log2p1(fullExpr[1]), color: IMMGEN_COLORS['CB(DZ)'], label: 'CB(DZ)' },
    { y: log2p1(fullExpr[2]), color: IMMGEN_COLORS['CC(LZ)'], label: 'CC(LZ)' },
    { y: log2p1(fullExpr[3]), color: IMMGEN_COLORS.PB, label: 'PB' },
  ].map(r => ({
    type: 'line',
    x0: -0.5, x1: nGroups - 0.5,
    y0: r.y, y1: r.y,
    line: { color: r.color, width: 1.5, dash: 'dash' },
  })) : [];

  return (
    <div className="gene-detail">
      <div className="gene-header">
        <h2><em>{gene}</em></h2>
        {panelMemberships.length > 0 && (
          <div className="panel-tags">
            {panelMemberships.map(({ panel, subcat }) => (
              <span key={`${panel}-${subcat}`} className="panel-tag"
                    style={{ borderColor: data.subcatColors[subcat] || '#555' }}>
                {panel} / {subcat.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
        {Object.keys(deStats).length > 0 && (
          <div className="de-status">
            {Object.entries(deStats).map(([compId, de]) => (
              <span key={compId} className={`de-badge ${de.fdr < 0.05 ? (de.log2FC > 0 ? 'up' : 'down') : 'ns'}`}>
                {de.fdr < 0.05
                  ? `${de.log2FC > 0 ? '↑' : '↓'} ${de.label}: FC=${de.log2FC.toFixed(2)}, FDR=${de.fdr.toExponential(1)}`
                  : `NS (${de.label}): FC=${de.log2FC.toFixed(2)}`}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="gene-plots">
        {/* Bar chart */}
        <div className="gene-plot-card">
          <h3>Expression Across {nCols} Conditions</h3>
          <Plot
            data={[barTrace]}
            layout={{
              width: Math.max(500, 300 + nCols * 30), height: 300,
              paper_bgcolor: 'transparent',
              plot_bgcolor: '#fafafa',
              margin: { l: 50, r: 20, t: 10, b: 90 },
              xaxis: {
                tickangle: -45,
                tickfont: { size: 9, color: '#555' },
                showline: true,
                linecolor: '#ccc',
              },
              yaxis: {
                title: { text: 'log₂(TPM+1)', font: { size: 10, color: '#777' } },
                tickfont: { size: 9, color: '#555' },
                gridcolor: '#eee',
                zeroline: false,
                showline: true,
                linecolor: '#ccc',
              },
              showlegend: false,
            }}
            config={{ displayModeBar: false }}
          />
        </div>

        {/* Strip plot */}
        {samples && (
          <div className="gene-plot-card">
            <h3>Individual Samples</h3>
            <Plot
              data={stripTraces}
              layout={{
                width: Math.max(450, 250 + nGroups * 35), height: 300,
                paper_bgcolor: 'transparent',
                plot_bgcolor: '#fafafa',
                margin: { l: 50, r: 20, t: 10, b: 60 },
                xaxis: {
                  tickvals: tickVals,
                  ticktext: STRIP_GROUP_LABELS,
                  tickfont: { size: 7, color: '#555' },
                  range: [-0.5, nGroups - 0.5],
                  zeroline: false,
                  fixedrange: true,
                  showline: true,
                  linecolor: '#ccc',
                },
                yaxis: {
                  title: { text: 'log₂(TPM+1)', font: { size: 10, color: '#777' } },
                  tickfont: { size: 9, color: '#555' },
                  gridcolor: '#eee',
                  zeroline: false,
                  fixedrange: true,
                  showline: true,
                  linecolor: '#ccc',
                },
                shapes: refShapes,
                showlegend: false,
                hovermode: 'closest',
              }}
              config={{ displayModeBar: false }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
